import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Grid from "@mui/material/Grid";
import { isEmpty, omitBy } from "lodash";

import { actions, selectors } from "rdx";
import leadMessages from "rdx/modules/leads/messages";
import * as opts from "lib/misc/dropdownOptions";
import { homeownerInterestOptions } from "lib/misc/homeownerInterestOptions";
import { formatPhoneForPrefixInput, convertHtmlToPlainText } from "lib/helpers";
import { useBatteryOnlyCoverage } from "hooks/useBatteryOnlyCoverage";
import { useSmartForm } from "hooks/useSmartForm";
import useRouteMatcher from "hooks/useRouteMatcher";
import { CustomFieldT } from "types/Project/interfaces";

import EnterpriseCustomFields from "containers/Sales/projects/components/EnterpriseCustomFields";
import Banners from "components/Banners";
import { createBannerDetails } from "components/Banners/helpers";
import SmartForm from "components/SmartForm";
import {
  SmartFormTextField,
  SmartFormDropdown,
  SmartFormPhone,
  SmartFormSegmentedControl,
  SmartFormSwitch,
  SmartFormTextarea,
} from "components/mui-styled-components/SmartFormElements";
import SmartFormAddressAutocomplete, {
  AddressSuggestionT,
} from "components/mui-styled-components/SmartFormElements/SmartFormAddressAutocomplete";
import ConfirmModal from "components/mui-styled-components/Modals/ConfirmModal";
import DuplicateLeadModal from "components/mui-styled-components/Modals/DuplicateLeadModal";

import { AddUpdateLeadFormT, schema, blankFormData } from "./LeadFormData";

import * as St from "../SiteInfoForm/styles";

import * as staticData from "./LeadFormData";
import * as S from "./styles";

type LeadSummaryFormT = {
  reference: React.LegacyRef<HTMLButtonElement>;
  saveRef: React.LegacyRef<HTMLButtonElement>;
  currentTab: number;
  customFields: CustomFieldT[];
  powurFields: CustomFieldT[];
  enterpriseName: string;
  setCurrentTab: (tabIdx: number) => void;
  setIsSubmitDisabled: (bool: boolean) => void;
  setIsFormSubmitting: (bool: boolean) => void;
  setIsFormTouched: (bool: boolean) => void;
  setIsLeadInfoComplete: (bool: boolean) => void;
};

const LeadSummaryForm = ({
  reference,
  saveRef,
  currentTab,
  setCurrentTab,
  setIsSubmitDisabled,
  setIsFormSubmitting,
  setIsFormTouched,
  setIsLeadInfoComplete,
  customFields,
  powurFields,
  enterpriseName,
}: LeadSummaryFormT) => {
  const dispatch = useDispatch();
  const leadId: number = useSelector(selectors.selectLeadToProposalDrawerLeadId);
  const leadDetails = useSelector(selectors.selectLeadToProposalLeadDetails);
  const latestMessage = useSelector(selectors.getLatestMessageEvt);
  const isDrawerOpen = useSelector(selectors.selectLeadToProposalDrawerVisible);
  const submittableStatus = leadDetails?.submittableStatus?.status;
  const isNonServiceable =
    !!leadId && (submittableStatus === "unavailable" || (!leadDetails?.submitted && submittableStatus !== "ok"));
  const [duplicateLeadModalOpen, setDuplicateLeadModalOpen] = useState<boolean>(false);
  const [serviceAreaWarning, setServiceAreaWarning] = useState<string | null>(null);
  const [serviceAreaConfirmed, setServiceAreaConfirmed] = useState<boolean>(false);
  const [showContinueErrors, setShowContinueErrors] = useState<boolean>(false);

  const { isEnterprise, isAdmin } = useRouteMatcher();
  const addressFieldsRequired = !!leadId;

  const { methods, getSmartFormProps } = useSmartForm<AddUpdateLeadFormT>({
    schema,
    useFormProps: { defaultValues: blankFormData, mode: "all", reValidateMode: "onChange" },
  });

  const {
    watch,
    reset,
    setValue,
    getValues,
    handleSubmit,
    formState: { errors, isDirty },
  } = methods;

  const values = watch();
  const isAddressMissing = !values.street || !values.city || !values.state || !values.zip;
  const addressFieldsIncomplete = addressFieldsRequired && isAddressMissing;
  const isPhoneMissing = !values.phone?.phoneNumber;
  const isLeadInfoIncomplete = isAddressMissing || isPhoneMissing;
  const continueErrorFor = (isMissing: boolean, message: string) =>
    showContinueErrors && isMissing ? message : undefined;
  const streetContinueError = continueErrorFor(!values.street, "Street address is required to continue.");
  const cityContinueError = continueErrorFor(!values.city, "City is required to continue.");
  const stateContinueError = continueErrorFor(!values.state, "State is required to continue.");
  const phoneContinueError = continueErrorFor(isPhoneMissing, "Phone number is required to continue.");
  const phoneErrorMessage =
    errors.phone?.phoneNumber?.message || errors.phone?.phonePrefix?.message || phoneContinueError;

  const { isCovered: batteryOnlyCovered } = useBatteryOnlyCoverage(values.zip);
  const isSavedAsBattery = !!leadId && !!leadDetails?.homeownerInterest && leadDetails.homeownerInterest !== "solar";
  const showHomeownerInterest = batteryOnlyCovered || isSavedAsBattery;

  useEffect(() => {
    if (leadId || showHomeownerInterest || values.homeownerInterest === "solar") return;

    setValue("homeownerInterest", "solar");
  }, [leadId, showHomeownerInterest, values.homeownerInterest, setValue]);

  useEffect(() => {
    if (!!leadId && !isEmpty(leadDetails)) {
      reset({
        firstName: leadDetails.firstName || "",
        lastName: leadDetails.lastName || "",
        email: leadDetails.email || "",
        phone: formatPhoneForPrefixInput(leadDetails.phone),
        locale: leadDetails.locale || "",
        street: leadDetails?.address?.street || "",
        city: leadDetails?.address?.city || "",
        state: leadDetails?.address?.state || "",
        zip: leadDetails?.address?.zip || "",
        category: leadDetails.category || "",
        homeownerInterest: leadDetails.homeownerInterest || "solar",
        leadSource: determineLeadSource(leadDetails.leadSource),
        altSource: leadDetails.leadSource || "",
        copilot: leadDetails.copilot,
        homeownerNotes: convertHtmlToPlainText(leadDetails?.homeownerNotes),
      });
    }
  }, [leadDetails, !!leadId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setIsSubmitDisabled(!isDirty && isNonServiceable);
    setIsFormTouched(isDirty);
  }, [isDirty, isNonServiceable]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const reqdCreateFieldsPresent = !!values.firstName && !!values.lastName && !!values.email && !!values.zip;
    setIsSubmitDisabled(!reqdCreateFieldsPresent);
  }, [values.firstName, values.lastName, values.email, values.zip, setIsSubmitDisabled]);

  useEffect(() => {
    setIsLeadInfoComplete(!isLeadInfoIncomplete);
    if (!isLeadInfoIncomplete) {
      setShowContinueErrors(false);
    }
  }, [isLeadInfoIncomplete, setIsLeadInfoComplete]);

  useEffect(() => {
    if (isDrawerOpen && latestMessage.message === leadMessages.DUPLICATE_LEAD_FOUND) {
      setDuplicateLeadModalOpen(true);
    }
    if (isDrawerOpen && latestMessage.message === leadMessages.SERVICE_AREA_NOT_SERVICED) {
      setServiceAreaWarning(latestMessage.warning);
    }
  }, [latestMessage]); // eslint-disable-line react-hooks/exhaustive-deps

  const determineLeadSource = (source: string): string => {
    if (!source) return "";
    if (opts.leadSourceValues.includes(source)) {
      return source;
    }
    return "Other";
  };

  const formatFormValues = () => {
    const hasAddressChanged =
      !!leadId && (leadDetails.address.street !== values.street || leadDetails.address.zip !== values.zip);
    const shouldSaveLatLong = !!leadId && leadDetails.isAddressConfirmed;
    return {
      lead: {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        phone: values.phone.phoneNumber ? `${values.phone.phonePrefix}${values.phone.phoneNumber}` : null,
        locale: values.locale,
        category: values.category,
        copilot: values.copilot,
        homeownerInterest: values.homeownerInterest,
        homeownerNotes: values.homeownerNotes,
        leadSource: values.leadSource === "Other" ? values.altSource : values.leadSource,
      },
      address: {
        street: values.street,
        city: values.city,
        state: values.state,
        zip: values.zip,
        ...(shouldSaveLatLong && {
          // invalidate lat/long if address changes
          // persist if specific lat/long has been confirmed by user
          latitude: hasAddressChanged ? null : leadDetails.address.lat,
          longitude: hasAddressChanged ? null : leadDetails.address.long,
        }),
      },
      ...(hasAddressChanged && {
        siteInfo: {
          isAddressConfirmed: false,
        },
      }),
    };
  };

  const updateExistingLead = () => {
    const { lead, address, siteInfo } = formatFormValues();
    dispatch(
      actions.updateLead({
        leadId,
        lead,
        address,
        enterprise: isEnterprise,
        admin: isAdmin,
        siteInfo,
      }),
    );
  };

  const createNewLead = (confirm = false, confirmServiceArea = false) => {
    const { lead, address } = formatFormValues();
    dispatch(
      actions.createNewLead({
        lead,
        // The backend fills a missing state in from the zip, but treats "" as a real state
        // and then rejects the zip as outside the service area.
        address: omitBy(address, (value) => value === ""),
        enterprise: isEnterprise,
        confirm,
        confirmServiceArea,
      }),
    );
  };

  const saveLead = (): void => {
    dispatch(
      actions.setAlertMessageVisible({
        message: "Submitting. . .",
        severity: "info",
        duration: 30000,
      }),
    );
    setIsSubmitDisabled(true);
    setIsFormSubmitting(true);
    if (leadId) {
      updateExistingLead();
    } else {
      setServiceAreaConfirmed(false);
      createNewLead();
    }
  };

  const onSave = (): void => {
    saveLead();
  };

  const onSubmit = (): void => {
    if (leadId && isLeadInfoIncomplete) {
      setShowContinueErrors(true);
    }
    if (isDirty) {
      saveLead();
    } else if (!isNonServiceable && !isLeadInfoIncomplete) {
      setCurrentTab(currentTab + 1);
    }
  };

  const onError = (e) => {
    dispatch(
      actions.setAlertMessageVisible({
        message: `Invalid Fields: ${Object.keys(e).join(", ")}`,
        severity: "warning",
      }),
    );
  };

  const handleDuplicateLeadConfirm = () => {
    createNewLead(true, serviceAreaConfirmed);
    setDuplicateLeadModalOpen(false);
  };
  const handleServiceAreaConfirm = () => {
    setServiceAreaWarning(null);
    setServiceAreaConfirmed(true);
    createNewLead(false, true);
  };
  const handleServiceAreaCancel = () => {
    setServiceAreaWarning(null);
    setIsFormSubmitting(false);
    setIsSubmitDisabled(false);
  };

  const handleCloseDuplicateLeadModal = () => {
    setDuplicateLeadModalOpen(false);
    setIsFormSubmitting(false);
    setIsSubmitDisabled(false);
  };

  const handleAutocompleteSelect = (_: React.SyntheticEvent<Element, Event>, value: AddressSuggestionT): void => {
    if (value) {
      setValue("street", value.street);
      setValue("city", value.city);
      setValue("state", value.state);
      setValue("zip", value.zip);
    }
  };

  const clearAddressValues = (): void => {
    setValue("street", "");
    setValue("city", "");
    setValue("state", "");
    setValue("zip", "");
  };

  return (
    <SmartForm {...getSmartFormProps({})}>
      <Banners bannerDetails={createBannerDetails(leadDetails.showThdBanner)} />
      {powurFields.length > 0 && (
        <St.CustomFieldsContainer>
          <St.StyledSectionLabel>Custom Fields</St.StyledSectionLabel>
          <EnterpriseCustomFields customFields={powurFields} />
          <S.StyledDivider />
        </St.CustomFieldsContainer>
      )}
      {customFields.length > 0 && (
        <St.CustomFieldsContainer>
          <St.StyledSectionLabel>{enterpriseName} Fields</St.StyledSectionLabel>
          <EnterpriseCustomFields customFields={customFields} />
          <S.StyledDivider />
        </St.CustomFieldsContainer>
      )}
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <S.StyledSectionLabel>Homeowner Info</S.StyledSectionLabel>
        </Grid>
        <Grid item xs={6}>
          <SmartFormTextField
            name="firstName"
            label="First Name"
            error={!!errors.firstName}
            helperText={errors.firstName?.message}
            required
          />
        </Grid>
        <Grid item xs={6}>
          <SmartFormTextField
            name="lastName"
            label="Last Name"
            error={!!errors.lastName}
            helperText={errors.lastName?.message}
            required
          />
        </Grid>
        <Grid item xs={12}>
          <SmartFormTextField
            name="email"
            label="Email"
            error={!!errors.email}
            helperText={errors.email?.message}
            required
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <SmartFormPhone name="phone" label="Phone" error={!!phoneErrorMessage} helperText={phoneErrorMessage} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <SmartFormDropdown
            name="locale"
            mainLabel="Language Preference"
            dropdownOptions={opts.languageOptions}
            error={!!errors.locale}
            helperText={errors.locale?.message}
          />
        </Grid>
      </Grid>
      <S.StyledDivider />
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <S.StyledSectionLabel>Physical Address</S.StyledSectionLabel>
          {addressFieldsIncomplete && <S.NoteUrgent>Required to progress to lead qualification</S.NoteUrgent>}
        </Grid>
        <Grid item xs={12}>
          <SmartFormAddressAutocomplete
            name="street"
            label="street address"
            getValues={getValues}
            handleAutocompleteSelect={handleAutocompleteSelect}
            clearValues={clearAddressValues}
            required={addressFieldsRequired}
            hasRedHighlight={addressFieldsRequired && !values.street}
            error={!!streetContinueError}
            helperText={streetContinueError}
          />
        </Grid>
        <Grid item xs={8} sm={5}>
          <SmartFormTextField
            label="city"
            name="city"
            required={addressFieldsRequired}
            hasRedHighlight={addressFieldsRequired && !values.city}
            error={!!cityContinueError}
            helperText={cityContinueError}
          />
        </Grid>
        <Grid item xs={4} sm={3}>
          <SmartFormDropdown
            dropdownOptions={opts.stateOptions()}
            renderValue={(selected) => <>{selected}</>}
            mainLabel="state"
            name="state"
            required={addressFieldsRequired}
            hasRedHighlight={addressFieldsRequired && !values.state}
            error={!!stateContinueError}
            helperText={stateContinueError}
          />
        </Grid>
        <Grid item xs={6} sm={4}>
          <SmartFormTextField
            label="zipcode"
            name="zip"
            required
            error={!!errors.zip}
            helperText={errors.zip?.message}
          />
        </Grid>
        <Grid item xs={6}>
          <SmartFormDropdown
            dropdownOptions={opts.categoryOptions}
            mainLabel="Category"
            name="category"
            error={!!errors.category}
            helperText={errors.category?.message}
          />
        </Grid>
        <Grid item xs={12}>
          <S.PuertoRicoNote>{staticData.puertoRicoNote}</S.PuertoRicoNote>
        </Grid>
      </Grid>
      {showHomeownerInterest && (
        <>
          <S.StyledDivider />
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <S.HomeownerInterestCard>
                <S.StyledSectionLabel>What is the homeowner looking for?</S.StyledSectionLabel>
                <SmartFormSegmentedControl
                  name="homeownerInterest"
                  options={homeownerInterestOptions}
                  sx={{ marginTop: "14px" }}
                />
              </S.HomeownerInterestCard>
            </Grid>
          </Grid>
        </>
      )}
      <S.StyledDivider />
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <S.StyledSectionLabel>Homeowner Notes</S.StyledSectionLabel>
        </Grid>
        <Grid item xs={12}>
          <S.StyledHomeownerNote>
            Add detailed notes for yourself or your closer. <S.Blue>Learn more</S.Blue>
          </S.StyledHomeownerNote>
        </Grid>
        <Grid item xs={12}>
          <SmartFormTextarea
            name="homeownerNotes"
            mainLabel="Notepad"
            mainLabelStyle={{ ...S.FormLabel, textTransform: "uppercase" }}
            height={132}
          />
        </Grid>
        <Grid item xs={12} sm={8}>
          <SmartFormDropdown
            dropdownOptions={opts.leadSourceOptions}
            mainLabel="Lead Source"
            name="leadSource"
            error={!!errors.leadSource}
            helperText={errors.leadSource?.message}
          />
        </Grid>
        {values.leadSource === "Other" && (
          <Grid item xs={12}>
            <SmartFormTextField
              name="altSource"
              label="Source"
              error={!!errors.altSource}
              helperText={errors.altSource?.message}
            />
          </Grid>
        )}
      </Grid>
      {!!leadId && (
        <>
          <S.StyledDivider sx={{ marginTop: "40px" }} />
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <S.StyledSectionLabel>Copilot Program</S.StyledSectionLabel>
            </Grid>
            <Grid item xs={12}>
              <S.StyledCopilotNote>{staticData.copilotNote}</S.StyledCopilotNote>
            </Grid>
            <Grid item xs={12}>
              <SmartFormSwitch
                name="copilot"
                switchText={staticData.copilotText}
                checked={getValues("copilot") || false}
                error={!!errors.copilot}
                helperText={errors.copilot?.message}
              />
            </Grid>
          </Grid>
        </>
      )}
      <button ref={reference} style={{ display: "none" }} type="button" onClick={handleSubmit(onSubmit, onError)} />
      <button ref={saveRef} style={{ display: "none" }} type="button" onClick={handleSubmit(onSave, onError)} />
      <DuplicateLeadModal
        isModalOpen={duplicateLeadModalOpen}
        handleCloseModal={handleCloseDuplicateLeadModal}
        onConfirm={handleDuplicateLeadConfirm}
      />
      <ConfirmModal
        open={!!serviceAreaWarning}
        title="This area isn't serviced yet"
        handleConfirm={handleServiceAreaConfirm}
        handleCancel={handleServiceAreaCancel}
        confirmButtonText="Save lead"
        cancelButtonText="Cancel"
      >
        {serviceAreaWarning}
      </ConfirmModal>
    </SmartForm>
  );
};

export default LeadSummaryForm;
